/**
 * SideTab — a slim vertical rectangle anchored to the right edge of the
 * viewport with its label written top-to-bottom. Sits inside the page's
 * natural right margin so it doesn't cover content. Click to fire the
 * action (open the panel/modal).
 *
 * Stack multiple tabs by giving each a different `bottom` offset, or use
 * the (planned) `<SideTabStack>` wrapper.
 */
import { type ReactNode } from 'react';

interface SideTabProps {
  /** Visible vertical text. Top-to-bottom. */
  label: string;
  /** Click handler — typically opens the associated panel/modal. */
  onClick: () => void;
  /** Tooltip + aria-label. Defaults to `label`. */
  title?: string;
  /** Vertical anchor in pixels from the bottom of the viewport. */
  bottom: number;
  /** Visual accent — drives the text color and the hover glow. */
  accent?: 'cyan' | 'amber' | 'mono';
  /** Optional small dot rendered at the top (e.g. unread / beta indicator). */
  badge?: 'amber' | 'cyan' | null;
  /** Hide the tab (parent owns the visibility). */
  hidden?: boolean;
  /** Optional trailing icon (rendered above the text), kept for compat. */
  icon?: ReactNode;
  /** Tab width in pixels. Default 22 — match the original TERRA slot. */
  width?: number;
}

const ACCENT_CLASSES: Record<NonNullable<SideTabProps['accent']>, {
  border: string;
  textColor: string;
  shadow: string;
  hoverBorder: string;
  hoverText: string;
}> = {
  cyan: {
    border: 'border-cyan-500/30',
    textColor: 'text-cyan-300/85',
    shadow: 'hover:shadow-[0_0_16px_rgba(6,182,212,0.25)]',
    hoverBorder: 'hover:border-cyan-400/70',
    hoverText: 'hover:text-cyan-200',
  },
  amber: {
    border: 'border-amber-500/30',
    textColor: 'text-amber-300/85',
    shadow: 'hover:shadow-[0_0_16px_rgba(251,191,36,0.2)]',
    hoverBorder: 'hover:border-amber-400/70',
    hoverText: 'hover:text-amber-200',
  },
  mono: {
    border: 'border-mono-700',
    textColor: 'text-mono-300',
    shadow: 'hover:shadow-[0_0_16px_rgba(148,163,184,0.18)]',
    hoverBorder: 'hover:border-mono-500',
    hoverText: 'hover:text-mono-100',
  },
};

export function SideTab({
  label,
  onClick,
  title,
  bottom,
  accent = 'mono',
  badge = null,
  hidden = false,
  width = 22,
}: SideTabProps) {
  if (hidden) return null;
  const a = ACCENT_CLASSES[accent];

  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      aria-label={title ?? label}
      style={{ bottom: `${bottom}px`, width: `${width}px` }}
      className={`
        group fixed right-0 z-[1200]
        flex flex-col items-center justify-center
        py-3
        rounded-l-md border-y border-l ${a.border} ${a.hoverBorder}
        bg-mono-900/95 backdrop-blur
        text-[10px] font-semibold tracking-[0.25em] uppercase
        ${a.textColor} ${a.hoverText}
        shadow-md ${a.shadow}
        transition-[border-color,box-shadow,color,transform] duration-200 ease-out
        hover:translate-x-[-2px]
        select-none
      `}
    >
      {/* Optional notification dot at the top of the strip */}
      {badge && (
        <span
          aria-hidden
          className={`mb-1 h-1.5 w-1.5 rounded-full ${
            badge === 'amber' ? 'bg-amber-400' : 'bg-cyan-400'
          }`}
        />
      )}
      {/* Vertical text — reads top-to-bottom (rotate head right). */}
      <span
        style={{
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
        }}
        className="whitespace-nowrap"
      >
        {label}
      </span>
    </button>
  );
}
